import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { Trans, useTranslation } from 'gatsby-plugin-react-i18next';
import { Hero } from 'lidya-common-components';
import { useCustomAction } from './customHooks/useCustomAction';
import { ContextLink } from '../common/contextLink';
import {
  useNavigationLinks,
} from '../../hooks/useNavigationLinks';
import { useGlobalData } from '../../providers/globalDataProvider/globalDataProvider.useGlobalData';
import { BackGroundComponent } from '../common/backgroundComponent';

function ImageComponent({
  imageFileName,
  headerImageExtraClass,
  imagePosition,
  imageTitle,
  imageAlt,
}) {
  return (
    <BackGroundComponent
      imageFileName={imageFileName}
      headerImageExtraClass={headerImageExtraClass}
      imgStyle={{ objectPosition: `${imagePosition} center` }}
      title={imageTitle}
      alt={imageAlt}
    />
  );
}

ImageComponent.propTypes = {
  imageTitle: PropTypes.string,
  imageAlt: PropTypes.string,
  imageFileName: PropTypes.string.isRequired,
  headerImageExtraClass: PropTypes.string.isRequired,
  imagePosition: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

ImageComponent.defaultProps = {
  imageTitle: undefined,
  imageAlt: undefined,
};

function ImageContainer({
  imageFileName,
  imageTitleTranslated,
  imageAltTranslated,
  headerImageExtraClass,
  imagePosition,
}) {
  return (
    <ImageComponent
      imageFileName={imageFileName}
      headerImageExtraClass={headerImageExtraClass}
      imagePosition={imagePosition}
      imageTitle={imageTitleTranslated}
      imageAlt={imageAltTranslated}
    />
  );
}

ImageContainer.propTypes = {
  imageTitleTranslated: PropTypes.string,
  imageAltTranslated: PropTypes.string,
  imageFileName: PropTypes.string.isRequired,
  headerImageExtraClass: PropTypes.string.isRequired,
  imagePosition: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

ImageContainer.defaultProps = {
  imageTitleTranslated: undefined,
  imageAltTranslated: undefined,
};

function HeroComp({
  imageFileName,
  imageTitle,
  imageAlt,
  primaryText,
  secondaryText,
  headerImageExtraClass,
  imagePosition,
  title,
  customLinks,
  contextLink,
  CustomActionArea,
}) {
  const { country, language } = useGlobalData();
  const links = useNavigationLinks({ country, language });
  const { t } = useTranslation();
  const imageTitleTranslated = t(imageTitle);
  const imageAltTranslated = t(imageAlt);
  const secondaryTextTranslated = t(secondaryText);
  const { CustomActionComponent, customActionText } = useCustomAction({ title, CustomActionArea });

  const PrimaryText = useCallback(() => (<Trans i18nKey={primaryText} />), [primaryText]);
  const Image = useCallback(() => (
    <ImageContainer
      imageFileName={imageFileName}
      imageTitleTranslated={imageTitleTranslated}
      imageAltTranslated={imageAltTranslated}
      headerImageExtraClass={headerImageExtraClass}
      imagePosition={imagePosition}
    />
  ), [headerImageExtraClass, imageAltTranslated, imageFileName, imagePosition, imageTitleTranslated]);

  return (
    <Hero
      primaryText={<PrimaryText />}
      secondaryText={secondaryTextTranslated}
      CustomAction={CustomActionComponent}
      customActionText={customActionText}
      navLinks={customLinks || links}
      CustomLink={contextLink}
      ImageComponent={Image}
    />
  );
}

HeroComp.propTypes = {
  headerImageExtraClass: PropTypes.string,
  imageFileName: PropTypes.string,
  imageTitle: PropTypes.string,
  imageAlt: PropTypes.string,
  imagePosition: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  primaryText: PropTypes.string,
  secondaryText: PropTypes.string,
  title: PropTypes.string,
  customLinks: PropTypes.arrayOf(PropTypes.shape({})),
  contextLink: PropTypes.oneOfType([PropTypes.bool, PropTypes.elementType]),
  CustomActionArea: PropTypes.elementType,
};

HeroComp.defaultProps = {
  imageFileName: '',
  imageTitle: '',
  imageAlt: '',
  primaryText: '',
  secondaryText: '',
  headerImageExtraClass: '',
  imagePosition: 'center',
  title: '',
  customLinks: undefined,
  contextLink: ContextLink,
  CustomActionArea: undefined,
};

export default HeroComp;
